import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { SMS_CONSENT_TEXT } from "@/lib/smsConsent";

// Published brand details for the platform-level page a mobile carrier reviews.
const PLATFORM = {
  site: "limolinkx.com",
  phone: "+1 979 337 9282",
  email: "support@limolinkx.com",
};

const Section = ({ n, title, children }) => (
  <section className="border-t border-ink-700 pt-8">
    <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Section {n}</p>
    <h2 className="mt-2 text-xl font-medium text-slate-100">{title}</h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-400">{children}</div>
  </section>
);

const Fact = ({ label, children }) => (
  <div className="flex flex-col gap-1 border-b border-ink-700/60 py-3 sm:flex-row sm:gap-6">
    <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500 sm:w-52 sm:shrink-0">{label}</span>
    <span className="min-w-0 break-words text-sm text-slate-200">{children}</span>
  </div>
);

export default function SmsOptIn() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!companyId) return;
    api.get(`/public/company/${companyId}`)
      .then(({ data }) => setCompany(data.company))
      .catch((e) => setError(errMsg(e)));
  }, [companyId]);

  if (companyId && !company && !error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }

  const platform = !companyId;
  const name = company?.name || PLATFORM_NAME;
  const handle = company?.handle || companyId || "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const bookingUrl = handle ? `${origin}/book/${handle}` : "";
  const riderUrl = handle ? `${origin}/ride/${handle}` : "";
  const sender = platform ? "a transportation company" : name;
  const exampleName = platform ? "ABC Executive Transportation" : name;
  const samples = [
    `${exampleName}: Your reservation for Tue Jun 23 at 8:30 AM from IAH Terminal C is confirmed. Track your car: ${origin}/track/a1b2c3 Reply STOP to opt out, HELP for help.`,
    `${exampleName}: Your chauffeur is on the way for your 8:30 AM pickup at IAH Terminal C. Live tracking: ${origin}/track/a1b2c3 Reply STOP to opt out.`,
    `${exampleName}: Your chauffeur has arrived at IAH Terminal C in a black SUV, plate ABC-1234. Reply STOP to opt out.`,
    `${exampleName}: Thank you for riding with us. Your trip is complete and a balance of $45.00 remains. Secure payment: ${origin}/pay/GZmkudm0 Reply STOP to opt out, HELP for help.`,
  ];

  return (
    <div data-testid="sms-optin-page" className="min-h-screen bg-ink-900 px-4 py-14 text-slate-200">
      <div className="mx-auto max-w-3xl">
        <Link to={handle ? `/book/${handle}` : "/"} data-testid="sms-optin-back"
          className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> {handle ? "Back to booking" : `Back to ${PLATFORM_NAME}`}
        </Link>
        <p className="mt-10 font-mono text-xs uppercase tracking-[0.28em] text-gold">
          Text messaging · proof of opt-in
        </p>
        <h1 data-testid="sms-optin-title" className="mt-3 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
          How passengers consent to our text messages
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-slate-400">
          This page is published for passengers and for the mobile carriers who vet our messaging
          programme. It sets out, in full, who we are, the exact wording a passenger agrees to, where
          that wording appears, and every message we send. {error && (
            <span data-testid="sms-optin-error" className="text-amber-300">
              Company details could not be loaded, so the generic programme terms are shown.
            </span>
          )}
        </p>
        <p className="mt-3 text-sm text-slate-400">
          A plain-HTML version of this page, including screenshots of the consent step, is published at{" "}
          <a href="/sms-optin.html" data-testid="sms-optin-static-link" className="text-gold hover:underline">
            /sms-optin.html
          </a>.
        </p>

        <div className="mt-10 space-y-8">
          <Section n="1" title="Who sends the messages">
            <div data-testid="sms-optin-business">
              <Fact label="Business name">{name}</Fact>
              {platform ? (
                <>
                  <Fact label="Website">
                    <a href={`https://${PLATFORM.site}`} className="text-gold hover:underline">{PLATFORM.site}</a>
                  </Fact>
                  <Fact label="Contact phone">{PLATFORM.phone}</Fact>
                  <Fact label="Contact email">
                    <a href={`mailto:${PLATFORM.email}`} className="text-gold hover:underline">{PLATFORM.email}</a>
                  </Fact>
                  <Fact label="Programme type">
                    Transactional service notifications. {PLATFORM_NAME} is the reservation and dispatch
                    software used by licensed chauffeured transportation companies, and sends these
                    messages on each company's behalf. Every message names the transportation company
                    the passenger booked with and concerns that passenger's own reservation. No
                    marketing, promotional or affiliate content is sent.
                  </Fact>
                  <Fact label="Where we collect consent">
                    On the public booking page of each transportation company using {PLATFORM_NAME}, at{" "}
                    <span className="font-mono text-xs">{PLATFORM.site}/book/&lt;company&gt;</span>, and in
                    the passenger app at{" "}
                    <span className="font-mono text-xs">{PLATFORM.site}/ride/&lt;company&gt;</span>. Each
                    company also publishes this same page at{" "}
                    <span className="font-mono text-xs">{PLATFORM.site}/legal/sms-optin/&lt;company&gt;</span>.
                  </Fact>
                </>
              ) : (
                <>
                  {company?.contact_phone || company?.phone
                    ? <Fact label="Contact phone">{company.contact_phone || company.phone}</Fact> : null}
                  {company?.contact_email || company?.email
                    ? <Fact label="Contact email">{company.contact_email || company.email}</Fact> : null}
                  {company?.address ? <Fact label="Business address">{company.address}</Fact> : null}
                  <Fact label="Programme type">
                    Transactional service notifications about a reservation the recipient made. No
                    marketing, promotional or affiliate content is sent.
                  </Fact>
                  {bookingUrl && (
                    <Fact label="Where we collect consent">
                      <a href={bookingUrl} data-testid="sms-optin-booking-link" target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-gold hover:underline">
                        {bookingUrl} <ExternalLink className="h-3 w-3" />
                      </a>
                    </Fact>
                  )}
                  <Fact label="Messages sent by">
                    {PLATFORM_NAME} ({PLATFORM.site}), the reservation and dispatch software we use,
                    sends these messages on our behalf. Support: {PLATFORM.phone}, {PLATFORM.email}.
                  </Fact>
                </>
              )}
            </div>
          </Section>

          <Section n="2" title="The exact wording a passenger agrees to">
            <p>
              Below is the consent control reproduced from {platform ? "every" : "our"} booking form,
              word for word. It is shown unticked, as every passenger first sees it. Consent is
              optional: a reservation can be completed without it, and it is never bundled into{" "}
              {platform ? "any" : "our"} terms of service.
            </p>
            <div data-testid="sms-optin-replica" className="rounded-lg border border-gold/30 bg-ink-800/60 p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                As it appears on the booking form
              </p>
              <label className="mt-4 flex items-start gap-3">
                <input type="checkbox" checked={false} readOnly aria-label="Text me trip updates at this number"
                  className="mt-1 h-4 w-4 accent-gold" />
                <span className="text-xs leading-relaxed text-slate-300">
                  {SMS_CONSENT_TEXT} See our{" "}
                  <Link to="/legal/sms" className="text-gold hover:underline">SMS terms</Link> and{" "}
                  <Link to="/legal/privacy" className="text-gold hover:underline">privacy policy</Link>.
                </span>
              </label>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                Unticked by default · never a condition of booking
              </p>
            </div>
          </Section>

          <Section n="3" title="Every way a passenger can opt in">
            <p>
              <span className="text-slate-200">1. The booking form.</span> Publicly reachable, no login
              required{platform
                ? ` at ${PLATFORM.site}/book/<company>`
                : bookingUrl ? ` at ${bookingUrl}` : ""}. The passenger enters their own mobile number
              and ticks the unticked checkbox shown above.
            </p>
            <figure data-testid="sms-optin-screenshot-booking" className="rounded-lg border border-ink-600 bg-ink-800/60 p-3">
              <img src="/optin-booking-form.png" loading="lazy" className="w-full rounded"
                alt="Screenshot of the booking form showing the name, email and mobile number fields with an unticked checkbox reading: Text me trip updates at this number. Up to ~6 messages per trip. Message and data rates may apply. Reply STOP to opt out, HELP for help." />
              <figcaption className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                The consent step on the public booking form
              </figcaption>
            </figure>
            <p>
              <span className="text-slate-200">2. Reservations taken by telephone.</span> The
              dispatcher reads this script aloud, word for word, before any text message is enabled:
            </p>
            <p data-testid="sms-optin-script" className="border-l-2 border-gold pl-4 text-sm italic leading-relaxed text-slate-200">
              “Would you like text updates for this trip at this mobile number? You'll get up to about
              six messages for this reservation — your confirmation, when your chauffeur is on the way,
              and when they arrive. Message and data rates may apply, you can reply STOP at any time to
              stop them, and it is not required in order to book. May I turn those on for you?”
            </p>
            <p>
              Consent is recorded only if the passenger answers yes. The dispatcher then ticks
              “Passenger agreed to receive trip updates by text at this number” on the reservation,
              which stores the consent with a date and time. If the passenger declines, the box stays
              unticked and the software will not text that number.
            </p>
            <figure data-testid="sms-optin-screenshot-dispatch" className="rounded-lg border border-ink-600 bg-ink-800/60 p-3">
              <img src="/optin-dispatch-form.png" loading="lazy" className="w-full rounded"
                alt="Screenshot of the dispatcher's reservation form showing the customer name and mobile number fields, the verbal consent script, and an unticked checkbox reading: Passenger agreed to receive trip updates by text at this number." />
              <figcaption className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                The consent step on a reservation taken by telephone
              </figcaption>
            </figure>
            <p>
              <span className="text-slate-200">3. Returning passengers in the app.</span> The same
              unticked checkbox and the same wording appear on every new booking
              {platform ? ` at ${PLATFORM.site}/ride/<company>` : riderUrl ? ` at ${riderUrl}` : ""}.
            </p>
            <p>
              We never text a passenger who has not consented, and consent for one reservation is
              never carried over to another person's number.
            </p>
          </Section>

          <Section n="4" title="Frequency, cost, STOP and HELP">
            <div data-testid="sms-optin-programme">
              <Fact label="Message frequency">Varies by trip; up to approximately six messages per reservation.</Fact>
              <Fact label="Cost">Message and data rates may apply. We do not charge for text messages.</Fact>
              <Fact label="Opting out">
                Reply STOP to any message to stop all texts to that number immediately. Opting out does
                not cancel a reservation — trip updates continue by email.
              </Fact>
              <Fact label="Getting help">
                Reply HELP for our help message, or contact {platform ? PLATFORM_NAME : "us"} using the
                phone number and email above.
              </Fact>
              <Fact label="Carriers">Mobile carriers are not liable for delayed or undelivered messages.</Fact>
            </div>
          </Section>

          <Section n="5" title="The messages we send">
            <p>
              Representative messages from this programme
              {platform
                ? `. The name leading each message is the transportation company the passenger booked with — shown here as "${exampleName}" — so the passenger always recognises the sender`
                : ""}:
            </p>
            <div data-testid="sms-optin-samples" className="space-y-3">
              {samples.map((text, i) => (
                <p key={i} data-testid={`sms-optin-sample-${i + 1}`}
                  className="rounded-lg border border-ink-600 bg-ink-800/60 p-4 font-mono text-[11px] leading-relaxed text-slate-300">
                  {text}
                </p>
              ))}
            </div>
          </Section>

          <Section n="6" title="Privacy">
            <p>
              Mobile numbers are collected only to send these service messages about the passenger's
              own trip{platform ? ` with ${sender}` : ""}. We do not sell or rent mobile numbers, and no
              mobile information is shared with third parties or affiliates for marketing or
              promotional purposes. Our full{" "}
              <Link to="/legal/privacy" className="text-gold hover:underline">privacy policy</Link> and{" "}
              <Link to="/legal/sms" className="text-gold hover:underline">SMS programme terms</Link> are
              published publicly.
            </p>
          </Section>
        </div>

        <div className="mt-12 flex flex-wrap gap-6 font-mono text-xs text-slate-500">
          <Link to="/legal/terms" className="hover:text-gold">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-gold">Privacy</Link>
          <Link to="/legal/sms" className="hover:text-gold">SMS terms</Link>
        </div>
      </div>
    </div>
  );
}
